import React, { useMemo, useState } from 'react';
import { FileUp, Plus, Trash2 } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import type { CreatePurchaseRequestPayload, NamedReference } from '../api';
import { formatMoney, referenceLabel } from './format';
import {
  emptyPurchaseItem,
  emptyPurchaseRequestForm,
  purchaseRequestPayload,
  validatePurchaseRequestForm,
  type PurchaseRequestFormValues,
} from '../validation/purchaseForms';

export interface PurchaseRequestSubmission {
  payload: CreatePurchaseRequestPayload;
  attachment?: { file: File; kind?: string; description?: string };
}

interface Props {
  costCenters: NamedReference[];
  clients: NamedReference[];
  projects: NamedReference[];
  loadingCatalogs?: boolean;
  submitting?: boolean;
  submitError?: string | null;
  initialValues?: PurchaseRequestFormValues;
  submitLabel?: string;
  onSubmit: (submission: PurchaseRequestSubmission) => void;
  onCancel: () => void;
}

const CATEGORIES = [
  'Tecnologia',
  'Facilities',
  'Serviços profissionais',
  'Marketing',
  'Recursos Humanos',
  'Financeiro',
  'Outros',
];

const ACCESS_OPTIONS: Array<{
  key: 'needsDataAccess' | 'needsSystemsAccess' | 'needsEnvironmentAccess' | 'needsInternalInformation';
  label: string;
}> = [
  { key: 'needsDataAccess', label: 'Dados pessoais ou corporativos' },
  { key: 'needsSystemsAccess', label: 'Sistemas internos' },
  { key: 'needsEnvironmentAccess', label: 'Ambientes físicos ou tecnológicos' },
  { key: 'needsInternalInformation', label: 'Informações internas' },
];

export function PurchaseRequestForm({
  costCenters,
  clients,
  projects,
  loadingCatalogs,
  submitting,
  submitError,
  initialValues,
  submitLabel = 'Salvar rascunho',
  onSubmit,
  onCancel,
}: Props) {
  const [values, setValues] = useState<PurchaseRequestFormValues>(() => initialValues ?? emptyPurchaseRequestForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentKind, setAttachmentKind] = useState('supporting_document');
  const [attachmentDescription, setAttachmentDescription] = useState('');

  const estimatedTotal = useMemo(
    () => values.items.reduce((total, item) => {
      const quantity = Number(item.quantity.replace(',', '.')) || 0;
      const price = Number(item.estimatedUnitPrice.replace(',', '.')) || 0;
      return total + quantity * price;
    }, 0),
    [values.items],
  );

  function setField<K extends keyof PurchaseRequestFormValues>(key: K, value: PurchaseRequestFormValues[K]) {
    setValues((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: '' }));
  }

  function setItem(index: number, field: keyof PurchaseRequestFormValues['items'][number], value: string) {
    setValues((previous) => ({
      ...previous,
      items: previous.items.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item),
    }));
    setErrors((previous) => ({ ...previous, [`items.${index}.${field}`]: '' }));
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const nextErrors = validatePurchaseRequestForm(values);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;
    onSubmit({
      payload: purchaseRequestPayload(values),
      ...(attachmentFile
        ? { attachment: { file: attachmentFile, kind: attachmentKind, description: attachmentDescription.trim() || undefined } }
        : {}),
    });
  }

  return (
    <form className="space-y-8" onSubmit={submit} noValidate>
      {submitError && <Alert variant="error">{submitError}</Alert>}

      <section aria-labelledby="purchase-identification-title" className="space-y-4 rounded-lg border border-gray-200 bg-white p-5">
        <div>
          <h2 id="purchase-identification-title" className="text-base font-semibold text-gray-900">Identificação e vínculo</h2>
          <p className="text-sm text-gray-500">Os vínculos usam os catálogos autorizados da plataforma.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <TextInput label="Título *" value={values.title} onChange={(event) => setField('title', event.target.value)} error={errors.title} />
          <TextInput label="Área solicitante *" value={values.requestingArea} onChange={(event) => setField('requestingArea', event.target.value)} error={errors.requestingArea} />
          <AutocompleteSelect
            label="Centro de custo *"
            options={costCenters.map((item) => ({ value: item.id, label: referenceLabel(item) }))}
            value={values.costCenterId}
            onChange={(value) => setField('costCenterId', value)}
            error={errors.costCenterId}
            disabled={loadingCatalogs}
          />
          <AutocompleteSelect
            label="Cliente (opcional)"
            options={clients.map((item) => ({ value: item.id, label: referenceLabel(item) }))}
            value={values.clientId}
            onChange={(value) => {
              setField('clientId', value);
              const selectedProject = projects.find((project) => project.id === values.projectId);
              if (selectedProject?.clientId && selectedProject.clientId !== value) setField('projectId', '');
            }}
            disabled={loadingCatalogs}
          />
          <AutocompleteSelect
            label="Projeto (opcional)"
            options={projects
              .filter((item) => !values.clientId || !item.clientId || item.clientId === values.clientId)
              .map((item) => ({ value: item.id, label: referenceLabel(item) }))}
            value={values.projectId}
            onChange={(value) => setField('projectId', value)}
            disabled={loadingCatalogs}
          />
          <AutocompleteSelect
            label="Categoria *"
            options={CATEGORIES.map((category) => ({ value: category, label: category }))}
            value={values.category}
            onChange={(value) => setField('category', value)}
            error={errors.category}
          />
          <AutocompleteSelect
            label="Tipo de aquisição *"
            options={[{ value: 'product', label: 'Produto' }, { value: 'service', label: 'Serviço' }]}
            value={values.acquisitionType}
            onChange={(value) => setField('acquisitionType', value as PurchaseRequestFormValues['acquisitionType'])}
          />
          <AutocompleteSelect
            label="Prioridade *"
            options={[{ value: 'low', label: 'Baixa' }, { value: 'normal', label: 'Normal' }, { value: 'high', label: 'Alta' }]}
            value={values.priority}
            onChange={(value) => setField('priority', value as PurchaseRequestFormValues['priority'])}
          />
          <AutocompleteSelect
            label="Criticidade *"
            options={[{ value: 'low', label: 'Baixa' }, { value: 'medium', label: 'Média' }, { value: 'high', label: 'Alta' }, { value: 'critical', label: 'Crítica' }]}
            value={values.criticality}
            onChange={(value) => setField('criticality', value as PurchaseRequestFormValues['criticality'])}
          />
          <TextInput label="Necessário até" type="date" value={values.neededBy} onChange={(event) => setField('neededBy', event.target.value)} error={errors.neededBy} />
          <AutocompleteSelect
            label="Situação orçamentária"
            options={[
              { value: 'pending_validation', label: 'Pendente de validação' },
              { value: 'planned', label: 'Prevista no orçamento' },
              { value: 'unplanned', label: 'Não prevista' },
            ]}
            value={values.budgetStatus}
            onChange={(value) => setField('budgetStatus', value)}
          />
        </div>
        <Textarea label="Justificativa *" rows={3} value={values.justification} onChange={(event) => setField('justification', event.target.value)} error={errors.justification} showCharCount minLength={10} />
      </section>

      <section aria-labelledby="purchase-scope-title" className="space-y-4 rounded-lg border border-gray-200 bg-white p-5">
        <h2 id="purchase-scope-title" className="text-base font-semibold text-gray-900">Escopo e requisitos</h2>
        <Textarea label="Escopo *" rows={4} value={values.scope} onChange={(event) => setField('scope', event.target.value)} error={errors.scope} showCharCount minLength={10} />
        <div className="grid gap-4 md:grid-cols-2">
          <Textarea label="Requisitos técnicos" value={values.technicalRequirements} onChange={(event) => setField('technicalRequirements', event.target.value)} />
          <Textarea label="Requisitos funcionais" value={values.functionalRequirements} onChange={(event) => setField('functionalRequirements', event.target.value)} />
        </div>
        <AutocompleteSelect
          label="Impacto esperado"
          options={[{ value: 'low', label: 'Baixo' }, { value: 'medium', label: 'Médio' }, { value: 'high', label: 'Alto' }, { value: 'critical', label: 'Crítico' }]}
          value={values.impact}
          onChange={(value) => setField('impact', value)}
        />
      </section>

      <section aria-labelledby="purchase-access-title" className="space-y-3 rounded-lg border border-gray-200 bg-white p-5">
        <div>
          <h2 id="purchase-access-title" className="text-base font-semibold text-gray-900">Acessos necessários</h2>
          <p className="text-sm text-gray-500">Marque somente o que o fornecedor precisará acessar durante a execução.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {ACCESS_OPTIONS.map(({ key, label }) => (
            <label key={key} className="flex items-start gap-2 rounded-md border border-gray-200 p-3 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={values[key]}
                onChange={(event) => setField(key, event.target.checked)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <Textarea label="Detalhes dos acessos" value={values.accessDetails} onChange={(event) => setField('accessDetails', event.target.value)} error={errors.accessDetails} />
      </section>

      <section aria-labelledby="purchase-items-title" className="space-y-4 rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 id="purchase-items-title" className="text-base font-semibold text-gray-900">Itens da solicitação</h2>
            <p className="text-sm text-gray-500">O valor estimado é calculado a partir dos itens: <strong>{formatMoney(estimatedTotal)}</strong>.</p>
          </div>
          <Button type="button" variant="secondary" size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setField('items', [...values.items, emptyPurchaseItem()])}>
            Adicionar item
          </Button>
        </div>
        {errors.items && <p role="alert" className="text-sm text-red-600">{errors.items}</p>}
        <div className="space-y-4">
          {values.items.map((item, index) => (
            <fieldset key={item.key} className="rounded-md border border-gray-200 p-4">
              <legend className="px-1 text-sm font-medium text-gray-700">Item {index + 1}</legend>
              <div className="grid gap-4 md:grid-cols-6">
                <div className="md:col-span-3">
                  <TextInput label="Descrição *" value={item.description} onChange={(event) => setItem(index, 'description', event.target.value)} error={errors[`items.${index}.description`]} />
                </div>
                <TextInput label="Quantidade *" type="number" min="0.0001" step="0.0001" value={item.quantity} onChange={(event) => setItem(index, 'quantity', event.target.value)} error={errors[`items.${index}.quantity`]} />
                <TextInput label="Unidade *" value={item.unit} onChange={(event) => setItem(index, 'unit', event.target.value)} error={errors[`items.${index}.unit`]} />
                <TextInput label="Valor unitário estimado" type="number" min="0" step="0.01" value={item.estimatedUnitPrice} onChange={(event) => setItem(index, 'estimatedUnitPrice', event.target.value)} error={errors[`items.${index}.estimatedUnitPrice`]} />
                <div className="md:col-span-5">
                  <Textarea label="Especificações" rows={2} value={item.specifications} onChange={(event) => setItem(index, 'specifications', event.target.value)} />
                </div>
                <div className="flex items-end justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Remover item ${index + 1}`}
                    icon={<Trash2 className="h-4 w-4" />}
                    disabled={values.items.length === 1}
                    onClick={() => setField('items', values.items.filter((_, itemIndex) => itemIndex !== index))}
                  >
                    Remover
                  </Button>
                </div>
              </div>
            </fieldset>
          ))}
        </div>
      </section>

      <section aria-labelledby="purchase-attachment-title" className="space-y-4 rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-2">
          <FileUp className="h-5 w-5 text-blue-600" aria-hidden />
          <h2 id="purchase-attachment-title" className="text-base font-semibold text-gray-900">Anexo inicial (opcional)</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block text-sm text-gray-700">
            <span className="mb-1 block font-medium">Arquivo</span>
            <input
              data-testid="purchase-attachment-file"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.xml,.txt"
              onChange={(event) => setAttachmentFile(event.target.files?.[0] ?? null)}
              className="w-full rounded-md border border-gray-300 px-3 py-2"
            />
          </label>
          <AutocompleteSelect
            label="Tipo do anexo"
            options={[
              { value: 'supporting_document', label: 'Documento de apoio' },
              { value: 'technical_specification', label: 'Especificação técnica' },
              { value: 'reference', label: 'Referência' },
            ]}
            value={attachmentKind}
            onChange={setAttachmentKind}
          />
        </div>
        <TextInput label="Descrição do anexo" value={attachmentDescription} onChange={(event) => setAttachmentDescription(event.target.value)} />
        <p className="text-xs text-gray-500">O arquivo será armazenado no repositório privado após a criação do rascunho.</p>
      </section>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" loading={submitting}>{submitLabel}</Button>
      </div>
    </form>
  );
}
